import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardBody,
    Flex,
    Heading,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Skeleton,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorMode,
    createStandaloneToast,
    Select,
    FormControl,
    FormLabel,
    Badge,
    Wrap,
    WrapItem,
    Tag,
    TagLabel,
    TagLeftIcon,
    TagRightIcon,
    TagCloseButton,
    Spinner,
    Input,
    VStack,
    ModalFooter,
    HStack,
    IconButton,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react';
import { MdSettings, MdCall, MdCallReceived, MdDelete, MdRefresh, MdSync } from 'react-icons/md';
import Navigation from '../components/Navigation';
import axios from '../utils/axiosConfig';
import customTheme from '../theme';

const PhoneNumberPage = () => {
    const { colorMode } = useColorMode();
    const { toast } = createStandaloneToast({ theme: customTheme });
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNumber, setSelectedNumber] = useState(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [assistants, setAssistants] = useState([]);
    const [saving, setSaving] = useState(false);
    const [phoneSyncData, setPhoneSyncData] = useState([]);

    const onSettingsClose = () => setIsSettingsOpen(false);

    const syncData = async () => {
        setLoading(true);
        try {
            const [phoneResponse, assistantsResponse, rawSyncData] = await Promise.all([
                axios.get('/api/protected/phone-numbers/sync'),
                axios.get('/api/protected/assistants'),
                axios.get('/api/protected/twilio-sync-status')
            ]);
            setPhoneNumbers(phoneResponse.data);
            setAssistants(assistantsResponse.data);
            setPhoneSyncData(rawSyncData.data.results);
            toast({
                title: 'Success',
                description: 'Phone numbers refreshed successfully',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to fetch data';
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [phoneResponse, assistantsResponse] = await Promise.all([
                axios.get('/api/protected/phone-numbers'),
                axios.get('/api/protected/assistants')
            ]);
            setPhoneNumbers(phoneResponse.data);
            setAssistants(assistantsResponse.data);

        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to fetch data';
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleTagAssistant = async (phoneNumberId, assistantId, direction) => {
        setSaving(true);
        try {
            const response = await axios.post('/api/protected/phone-numbers/tag', {
                phoneNumberId: selectedNumber._id,
                assistantId: assistantId === '' ? null : assistantId,
                direction,
            });

            setPhoneNumbers((prevPhoneNumbers) =>
                prevPhoneNumbers.map((phoneNumber) =>
                    phoneNumber._id === selectedNumber._id ? {
                        ...phoneNumber,
                        [direction === 'inbound' ? 'inboundAssistant' : 'outboundAssistant']:
                            assistantId === '' ? null : assistantId
                    } : phoneNumber
                )
            );

            setSelectedNumber(prev => ({
                ...prev,
                [direction === 'inbound' ? 'inboundAssistant' : 'outboundAssistant']:
                    assistantId === '' ? null : assistantId
            }));

            toast({
                title: 'Success',
                description: 'Assistant configuration updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
        } catch (error) {
            console.error('Error updating assistant:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to update assistant configuration',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setSaving(false);
        }
    };

    const syncTwilioWebhook = async (id) => {
        try {
            const response = await axios.post(`/api/protected/sync-twilio/${id}`);
            console.log(response.data);
            if(response && response.data.success){

                toast({
                    title: 'Success',
                    description: 'Synced to Twilio Successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true
                });

                const response = await axios.get('/api/protected/twilio-sync-status');
                if(response && response.data.success){
                    setPhoneSyncData(response.data.results);
                }
            }else {
                toast({
                    title: 'Error',
                    description: 'Failed in syncing to Twilio',
                    status: 'error',
                    duration: 3000,
                    isClosable: true
                });
            }
        } catch (error) {
            console.error('Error syncing to Twilio:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed in syncing to Twilio',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
    }

    return (
        <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
            <Navigation />
            <Box flex="1" p={6}>
                <Flex justify="space-between" align="center" mb={6}>
                    <Heading
                        size="lg"
                        color={colorMode === 'light' ? 'gray.800' : 'white'}
                    >
                        Phone Numbers
                    </Heading>
                    <Tooltip label="Refresh phone numbers">
                        <IconButton
                            icon={<MdRefresh />}
                            aria-label="Refresh phone numbers"
                            onClick={() => syncData()}
                            isLoading={loading}
                            colorScheme="brand"
                        />
                    </Tooltip>
                </Flex>

                <Card bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                    <CardBody>
                        {loading ? (
                            <Stack spacing={4}>
                                <Skeleton height="40px" />
                                <Skeleton height="40px" />
                                <Skeleton height="40px" />
                            </Stack>
                        ) : (
                            <Box overflowX="auto">
                                <Table variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Phone Number</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Configuration</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Sync Status</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {phoneNumbers.map((number) => {
                                            const sync = phoneSyncData.find((item) => item.sid === number.sid);
                                            const isSynced = sync?.synced;
                                            return (
                                            <Tr key={number.sid}>
                                                <Td color={colorMode === 'light' ? 'gray.800' : 'white'}>{number.phoneNumber}</Td>
                                                <Td>
                                                    <Wrap>
                                                        {number.inboundAssistant && (
                                                            <WrapItem>
                                                                <Tag colorScheme="brand" size="sm">
                                                                    <TagLeftIcon as={MdCallReceived} />
                                                                    <TagLabel>Inbound</TagLabel>
                                                                </Tag>
                                                            </WrapItem>
                                                        )}
                                                        {number.outboundAssistant && (
                                                            <WrapItem>
                                                                <Tag colorScheme="brand" size="sm">
                                                                    <TagLeftIcon as={MdCall} />
                                                                    <TagLabel>Outbound</TagLabel>
                                                                </Tag>
                                                            </WrapItem>
                                                        )}
                                                    </Wrap>
                                                </Td>
                                                <Td>
                                                    {sync ? (
                                                        isSynced ? (
                                                            <Tag colorScheme="green" size="sm">In Sync</Tag>
                                                        ) : (
                                                            <Tag colorScheme="red" size="sm">Out of Sync</Tag>
                                                        )
                                                        ) : (
                                                        <Tag colorScheme="gray" size="sm">Unknown</Tag>
                                                    )}
                                                </Td>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <Tooltip label="Settings">
                                                            <IconButton
                                                                icon={<MdSettings />}
                                                                aria-label="Phone number settings"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedNumber(number);
                                                                    setIsSettingsOpen(true);
                                                                }}
                                                                colorScheme="brand"
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="Sync Setting to Twilio">
                                                            <IconButton
                                                                icon={<MdSync />}
                                                                aria-label="Sync Setting to Twilio"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedNumber(number);
                                                                    syncTwilioWebhook(number._id);
                                                                }}
                                                                colorScheme="brand"
                                                            />
                                                        </Tooltip>
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        )}
                                    )}
                                    </Tbody>
                                </Table>
                            </Box>
                        )}
                    </CardBody>
                </Card>

                {/* Settings Modal */}
                <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} size="xl">
                    <ModalOverlay />
                    <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                        <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>Phone Number Settings</ModalHeader>
                        <ModalCloseButton color={colorMode === 'light' ? 'gray.800' : 'white'} />
                        <ModalBody pb={6}>
                            {selectedNumber && (
                                <Stack spacing={4}>
                                    <Text color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                        Phone Number: {selectedNumber.phoneNumber}
                                    </Text>

                                    <FormControl>
                                        <FormLabel color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Inbound Assistant</FormLabel>
                                        <Select
                                            value={selectedNumber.inboundAssistant || ''}
                                            onChange={(e) => handleTagAssistant(selectedNumber._id, e.target.value, 'inbound')}
                                            isDisabled={saving}
                                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                        >
                                            <option value="">None</option>
                                            {assistants.map((assistant) => (
                                                <option key={assistant._id} value={assistant._id}>
                                                    {assistant.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Outbound Assistant (Legacy). Not required anymore as you can specify the assistant via API</FormLabel>
                                        <Select
                                            value={selectedNumber.outboundAssistant || ''}
                                            onChange={(e) => handleTagAssistant(selectedNumber._id, e.target.value, 'outbound')}
                                            isDisabled={saving}
                                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                        >
                                            <option value="">None</option>
                                            {assistants.map((assistant) => (
                                                <option key={assistant._id} value={assistant._id}>
                                                    {assistant.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {saving && (
                                        <Flex align="center" gap={2}>
                                            <Spinner size="sm" />
                                            <Text color={colorMode === 'light' ? 'gray.800' : 'white'}>Saving changes...</Text>
                                        </Flex>
                                    )}
                                </Stack>
                            )}
                        </ModalBody>
                    </ModalContent>
                </Modal>


            </Box>
        </Flex>
    );
};

export default PhoneNumberPage;
